import { extractApiError, type HttpApiClient } from "@vana/core";
import {
  type BenefitNotification,
  type Refund,
} from "../../domain/entities/first-purchase";
import {
  ConfirmFirstPurchaseError,
  GetBenefitError,
  GetRefundError,
  NotifyBenefitError,
  UnnotifyBenefitError,
} from "../../domain/errors/errors";
import {
  type ConfirmFirstPurchaseParams,
  type FirstPurchaseRepository,
  type NotifyBenefitParams,
} from "../../domain/repos/first-purchase-repo";
import {
  type BenefitResponse,
  type ParameterResponse,
} from "../../infrastructure/responses/first-purchase-response";

// The business-parameter key for the first-purchase refund (backend identifier).
const REFUND_PARAMETER_KEY = "first_purchase_refund";

export class FirstPurchaseRepo implements FirstPurchaseRepository {
  constructor(private readonly apiClient: HttpApiClient) {}

  async getRefund(): Promise<Refund> {
    try {
      const response = await this.apiClient.get<ParameterResponse>(
        `v1/parameters/${REFUND_PARAMETER_KEY}`,
      );
      const data = response.data;
      if (!data) throw new GetRefundError("Empty refund response");
      return { value: data.value, kind: data.kind };
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new GetRefundError(message, apiError);
    }
  }

  async getBenefit(requestId: string): Promise<BenefitNotification> {
    try {
      const response = await this.apiClient.get<BenefitResponse>(
        `v1/requests/${requestId}/benefit-notification`,
      );
      return response.data ?? { notified: false };
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new GetBenefitError(message, apiError);
    }
  }

  async notifyBenefit({ requestId, lat, long }: NotifyBenefitParams): Promise<BenefitNotification> {
    try {
      const response = await this.apiClient.post<
        { lat: number; long: number },
        BenefitResponse
      >(`v1/requests/${requestId}/benefit-notification`, { lat, long });
      return response.data ?? { notified: true, lat, long };
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new NotifyBenefitError(message, apiError);
    }
  }

  async unnotifyBenefit(requestId: string): Promise<void> {
    try {
      await this.apiClient.delete(`v1/requests/${requestId}/benefit-notification`);
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new UnnotifyBenefitError(message, apiError);
    }
  }

  async confirmFirstPurchase({
    requestId,
    lat,
    long,
  }: ConfirmFirstPurchaseParams): Promise<void> {
    try {
      await this.apiClient.post<{ lat: number; long: number }, unknown>(
        `v1/requests/${requestId}/first-purchase`,
        { lat, long },
      );
    } catch (error) {
      const { message, apiError } = extractApiError(error);
      throw new ConfirmFirstPurchaseError(message, apiError);
    }
  }
}
